import React, { useEffect, useState, useRef, useMemo } from 'react'
import { useFormik } from "formik"
import * as Yup from "yup"
import { useNavigate } from 'react-router-dom'

// components
import {
  CButton,
  CCard,
  CCardBody,
  CCardGroup,
  CCol,
  CContainer,
  CForm,
  CFormInput,
  CInputGroup,
  CInputGroupText,
  CModal,
  CModalBody,
  CModalFooter,
  CModalHeader,
  CModalTitle,
  CRow,
  CSpinner
} from '@coreui/react-pro'
import { Link } from 'react-router-dom'
import Cookies from 'js-cookie';

// components
import FormPassword from '../../../components/form/general-input-form/FormPassword'
import FormCaptcha from '../../../components/form/general-input-form/FormCaptcha'

// helpers
import Axios from '../../../helpers/axios'
import useToastMessage from 'src/helpers/toast-message'
import { useResponsive } from 'src/helpers/get-responsive'

const Login = () => {
  const navigate = useNavigate()
  const { showError, showSuccess } = useToastMessage();
  const { isDesktop } = useResponsive();

  const token = Cookies.get("ut")

  const captchaRef = useRef(null)

  const [isLoading, setIsLoading] = useState(false)
  const [isForgotPasswordLoading, setIsForgotPasswordLoading] = useState(false)
  const [isForgotPasswordModalVisible, setIsForgotPasswordModalVisible] = useState(false)
  const [isForgotPasswordRequested, setIsForgotPasswordRequested] = useState(false)
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('')
  const [forgotPasswordEmailError, setForgotPasswordEmailError] = useState('')
  const [isWrongEmailOrPassword, setIsWrongEmailOrPassword] = useState(false)
  const [wrongEmailOrPasswordMsg, setWrongEmailOrPasswordMsg] = useState('')
  const [isUserNotFound, setIsUserNotFound] = useState(false)
  const [userNotFoundMsg, setUserNotFoundMsg] = useState('')

  const loginSchema = Yup.object().shape({
    email: Yup.string()
        .email("Format email invalid.")
        .required("Please enter your email address."),
    password: Yup.string()
        .required("Please enter your password."),
    captcha: Yup.string().required("Please enter the captcha code."),
  })

  // Field yang harus valid dulu sebelum captcha ditampilkan.
  const BASE_FIELDS_KEYS = ['email', 'password']
  const baseFieldsSchema = loginSchema.pick(BASE_FIELDS_KEYS)

  const formik = useFormik({
    initialValues: {
      email: '',
      password: '',
      captcha: '',
    },
    validationSchema: loginSchema,
    validateOnBlur: true,
    validateOnChange: false,
    onSubmit: async (values, { setSubmitting, setFieldValue }) => {
      const isCaptchaValid = captchaRef.current?.validate(values.captcha)

      if (!isCaptchaValid) {
        showError('Captcha code is invalid. Please try again.')
        captchaRef.current?.refresh()
        setFieldValue('captcha', '')
        setSubmitting(false)
        return
      }

      handleSubmit(values)
      setSubmitting(false)
    },
  });

  const isBaseFieldsValid = useMemo(
    () => baseFieldsSchema.isValidSync({
      email: formik.values.email,
      password: formik.values.password,
    }),
    [formik.values.email, formik.values.password]
  )

  useEffect(() => {
    if (!isBaseFieldsValid && formik.values.captcha) {
      formik.setFieldValue('captcha', '')
    }
  }, [isBaseFieldsValid])

  const handleInputValue = (values) => {
    const payload = {
      email: values.email,
      password: values.password
    }

    return payload
  }

  const handleLogin = (payload) => {
    setIsLoading(true)
    Axios({
      method: "post",
      url: '/api/sso/v1/users/login',
      data: payload
    })
      .then(({ data }) => {
        if (data) {
          let dataUser = {
            id: data?.id,
            profile: data?.profile,
            username: data?.username,
            role_id: data?.role_id[0],
            role_user: data?.roles?.length > 0 ? data.roles[0].title : null,
            email: data?.email
          }
          localStorage.setItem("user", JSON.stringify(dataUser))
          if (data?.role_id && data?.role_id[0] == '1') {
            Axios({
              method: "get",
              url: `/api/tracker/v1/generalsetting`,
            })
              .then(({ data: dataGeneralSetting }) => {
                const dataSettings = dataGeneralSetting?.value;
                if (dataSettings) {
                  localStorage.setItem("general_setting", JSON.stringify(dataSettings))
                }
              })
              .catch((err) => {
                console.error(err);
              })
              .finally(() => {
                navigate('/')
              })
          } else {
            navigate('/client/list')
          }
        }
      })
      .catch((err) => {
        console.error(err);
        if (err && err.response && err.response.data) {
          const { response: { data: { message } = {} } = {} } = err || {};
          if (message) {
            if (message === "wrong-email-or-password") {
              setIsWrongEmailOrPassword(true);
              setWrongEmailOrPasswordMsg('Wrong email or password')
              showError('Wrong email or password. Please try again.')
            }
            if (message === 'user-not-found') {
              setIsUserNotFound(true);
              setUserNotFoundMsg('User not found')
              showError('User not found. Please check your email or register first.')
            }
            if (message === 'user-not-verified') {
              showError('Please verify your email first!')
            }
            if (message === 'user-not-active') {
              showError('Your account is waiting for approval')
            }
          }
        }
      })
      .finally(() => setIsLoading(false))
  }

  const handleSubmit = (values) => {
    const inputPayload = handleInputValue(values)
    handleLogin(inputPayload)
  }

  const clearForgotPasswordState = () => {
    setIsForgotPasswordRequested(false)
    setForgotPasswordEmail('')
    setForgotPasswordEmailError('')
    setIsForgotPasswordLoading(false)
  }

  const handleOpenForgotPasswordModal = () => {
    clearForgotPasswordState()
    setForgotPasswordEmail(formik.values.email || '')
    setIsForgotPasswordModalVisible(true)
  }

  const handleCloseForgotPasswordModal = () => {
    setIsForgotPasswordModalVisible(false)
    clearForgotPasswordState()
  }

  const validateForgotPasswordEmail = () => {
    if (!forgotPasswordEmail) {
      setForgotPasswordEmailError('Please enter your email address.')
      return false
    }

    const emailSchema = Yup.string().email('Format email invalid.')
    if (!emailSchema.isValidSync(forgotPasswordEmail)) {
      setForgotPasswordEmailError('Format email invalid.')
      return false
    }

    setForgotPasswordEmailError('')
    return true
  }

  const handleRequestForgotPasswordFromLogin = () => {
    const isValidEmail = validateForgotPasswordEmail()

    if (!isValidEmail) {
      return
    }

    setIsForgotPasswordLoading(true)

    Axios({
      method: 'post',
      url: '/api/tracker/v1/email/send/request-reset-password',
      data: {
        user: {
          email: forgotPasswordEmail,
        },
      },
    })
      .then(() => {
        setIsForgotPasswordRequested(true)
        showSuccess(`Request reset password has been sent to ${forgotPasswordEmail}`)
      })
      .catch((err) => {
        console.error(err)
        const errMsg = err?.response?.data?.message || 'Failed to send reset password request'
        showError(errMsg)
      })
      .finally(() => {
        setIsForgotPasswordLoading(false)
      })
  }

  useEffect(() => {
    if (token) {
      navigate('/')
    }
  }, [])

  return (
    <div className="bg-light min-vh-100 d-flex flex-row align-items-center">
      <CContainer>
        <CRow className="justify-content-center">
          <CCol md={6}>
            <CCardGroup>
              <CCard className="p-4">
                <CCardBody>
                  <CForm>
                    <h1>Login</h1>
                    <p className="text-medium-emphasis">Sign In To Your Account</p>
                    <CInputGroup className="mb-3">
                      <CInputGroupText>@</CInputGroupText>
                      <CFormInput
                        type='email'
                        id='email'
                        name='email'
                        placeholder="Email Address"
                        value={formik.values.email}
                        onChange={(e) => formik.handleChange(e)}
                        invalid={formik.errors.email || isWrongEmailOrPassword || isUserNotFound}
                        feedbackInvalid={formik.errors.email ? formik.errors.email : isWrongEmailOrPassword ? wrongEmailOrPasswordMsg : isUserNotFound ? userNotFoundMsg : null}
                        autoComplete='off'
                      />
                    </CInputGroup>
                    <FormPassword
                      id='password'
                      name='password'
                      placeholder="Password"
                      value={formik.values.password}
                      handleTextInput={formik.handleChange}
                      handleBlur={formik.handleBlur}
                      invalid={!!formik.errors.password}
                      feedbackInvalid={formik.errors.password}
                      className='mb-3'
                    />
                    {
                      isBaseFieldsValid && (
                        <FormCaptcha 
                          ref={captchaRef}
                          id='captcha'
                          name='captcha'
                          colSize={12}
                          className='mb-3'
                          placeholder='Input captcha code'
                          value={formik.values.captcha}
                          handleTextInput={formik.handleChange}
                          handleBlur={formik.handleBlur}
                          invalid={!!formik.errors.captcha}
                          feedbackInvalid={formik.errors.captcha}
                        />
                      )
                    }
                    <CRow>
                      <CCol xs={6} lg={3}>
                        <CButton
                          color="dark"
                          className="px-4"
                          onClick={() => formik.handleSubmit()}
                        >
                          {isLoading && (
                            <CSpinner
                              className='me-2'
                              component="span"
                              color="light"
                              size="sm"
                              aria-hidden="true"
                            />
                          )}
                          Login
                        </CButton>
                      </CCol>
                      <CCol xs={6} lg={9} className="text-end">
                        <span
                          role="button"
                          tabIndex={0}
                          style={{ cursor: 'pointer' }}
                          onClick={handleOpenForgotPasswordModal}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              handleOpenForgotPasswordModal()
                            }
                          }}
                        >
                          Forgot password?
                        </span>
                      </CCol>
                    </CRow>
                  </CForm>
                </CCardBody>
              </CCard>
              <CCard className="text-white bg-dark" style={ isDesktop ? { width: '44%' } : {} }>
                <CCardBody className="text-center">
                  <div className="d-flex flex-column align-items-center justify-content-center h-100">
                    <h2>Sign up</h2>
                    <p>
                      Don't have an account?
                    </p>
                    <Link to="/register">
                      <CButton color="dark" className="mt-3" active tabIndex={-1}>
                        Register Here!
                      </CButton>
                    </Link>
                  </div>
                </CCardBody>
              </CCard>
            </CCardGroup>
          </CCol>
        </CRow>
      </CContainer>
      <CModal alignment="center" visible={isForgotPasswordModalVisible} onClose={handleCloseForgotPasswordModal}>
        <CModalHeader>
          <CModalTitle>Forgot Password</CModalTitle>
        </CModalHeader>
        <CModalBody>
          {
            !isForgotPasswordRequested ? (
              <>
                <p className="mb-3">Enter your email to request password reset link.</p>
                <CFormInput
                  type='email'
                  id='forgot-password-email'
                  name='forgot-password-email'
                  placeholder='Email Address'
                  value={forgotPasswordEmail}
                  onChange={(e) => {
                    setForgotPasswordEmail(e.target.value)
                    if (forgotPasswordEmailError) {
                      setForgotPasswordEmailError('')
                    }
                  }}
                  invalid={!!forgotPasswordEmailError}
                  feedbackInvalid={forgotPasswordEmailError}
                  autoComplete='off'
                />
              </>
            ) : (
              <>
                <p className="mb-2">Reset password email has been sent to <strong>{forgotPasswordEmail}</strong>.</p>
                <p className="mb-2">Next steps:</p>
                <ol className="mb-0 ps-3">
                  <li>Open your inbox and click the password reset link.</li>
                  <li>Create a new password with security requirements.</li>
                  <li>Confirm your new password.</li>
                  <li>You will be redirected back to login page.</li>
                </ol>
              </>
            )
          }
        </CModalBody>
        <CModalFooter>
          {
            !isForgotPasswordRequested ? (
              <>
                <CButton color="secondary" variant="outline" onClick={handleCloseForgotPasswordModal}>
                  Cancel
                </CButton>
                <CButton color="dark" onClick={handleRequestForgotPasswordFromLogin} disabled={isForgotPasswordLoading}>
                  {isForgotPasswordLoading && (
                    <CSpinner
                      className='me-2'
                      component="span"
                      color="light"
                      size="sm"
                      aria-hidden="true"
                    />
                  )}
                  Send Request
                </CButton>
              </>
            ) : (
              <CButton color="dark" onClick={handleCloseForgotPasswordModal}>
                Back to Login
              </CButton>
            )
          }
        </CModalFooter>
      </CModal>
    </div>
  )
}

export default Login
